import { Component, inject, OnInit, signal, TemplateRef, WritableSignal } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IServerSideDatasource, IServerSideGetRowsParams, RowSelectionOptions, SideBarDef } from 'ag-grid-community';
import { TabService } from 'src/app/services/components/tab/tab.service';
import { ActionButtonsComponent } from '../../components/action-buttons/action-buttons.component';
import { PopoverInfoComponent } from '../../components/popover-info/popover-info.component';
import { placements } from '@popperjs/core';
import { trigger } from '@angular/animations';
import { CustomBadgeRendererComponent } from 'src/app/components/custom-badge-renderer/custom-badge-renderer.component';
import { ChildComponent } from 'src/app/components/child/child.component';
import { DropdownCellEditorComponent } from '../../components/dropdown-cell-editor/dropdown-cell-editor.component';
import { RateListingsService } from 'src/app/services/modules/instant-rate-manager/rate-listings.service';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { GroupService } from 'src/app/services/modules/instant-rate-manager/group.service';
import Swal from 'sweetalert2';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { CreateRateModalComponent } from '../../components/create-rate-modal/create-rate-modal.component';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { BulkCreateRateModalComponent } from '../../components/bulk-create-rate-modal/bulk-create-rate-modal.component';
import { SavedSearchComponent } from 'src/app/components/saved-search/saved-search.component';
import { ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';

interface Tab {
  id: number;
  name: string;
  description?: string;
}

interface BulkUpdateField {
  field: string;
  apiKey: string;
  label: string;
  type: 'number' | 'text' | 'select';
  options?: string[];
}

@Component({
  selector: 'app-rate-listings',
  templateUrl: './rate-listings.component.html',
  styleUrl: './rate-listings.component.scss',
})
export class RateListingsComponent extends ChildComponent implements OnInit {
  rateManagerService = inject(RateManagerService);
  rateListingService = inject(RateListingsService);
  groupService = inject(GroupService);
  zoneService = inject(ZoneService);

  public gridApi: any;
  public viewFilterType: string = 'rate-listing';
  public module: string = 'instant-rate';

  @ViewChild('savedSearch') savedSearch!: SavedSearchComponent;
  @ViewChild('bulkUpdateModal') bulkUpdateModal!: TemplateRef<any>;

  selectedRowCount = 0;
  bulkUpdateField = '';
  bulkUpdateValue: string | number = '';
  bulkTransitDaysMode: 'update' | 'add_on_top' = 'update';

  readonly bulkTransitDaysModeOptions = [
    { value: 'update' as const, label: 'Update' },
    { value: 'add_on_top' as const, label: 'Add on top' },
  ];

  /**
   * STATE
   */
  public groupArray: Array<any> = [];
  public searchGroup = '';
  selectedGroup: number | null = null;
  public rateCategoryArray: Array<any> = [];
  public isAddingGroup = false;
  public newGroupName = '';
  suppressGridRefresh = false;
  public instantQuoteRateCategories: Array<any> = [];
  public activeTabIndex = 0;
  public hasNoZones = false;
  public totalRates = 0;
  public selectedCategory!: Tab;
  selectedCategoryId: number | null = null;

  // Inline edit tracking
  public pendingEdits: Map<
    string,
    {
      routeRateIds: number[];
      changes: Record<string, any>;
      originalValues: Record<string, any>;
    }
  > = new Map();

  get hasPendingEdits(): boolean {
    return this.pendingEdits.size > 0;
  }

  get selectedBulkFieldConfig(): BulkUpdateField | undefined {
    return this.getBulkUpdateFields().find((field) => field.field === this.bulkUpdateField);
  }

  get isTransitDaysBulkUpdate(): boolean {
    return this.bulkUpdateField === 'transit_days';
  }

  /**
   * GRID CONFIGURATION
   */
  public columnDefs: ColDef[] = [];
  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 150,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      filterOptions: [
        'contains',
        'equals',
      ],
      defaultOption: 'contains',
    },
    editable: (params: any) => {
      if (!params.data || !params.colDef.field) return false;
      const isRateColumn = params.colDef.cellRendererParams?.isRateColumn === true;
      if (isRateColumn) {
        const value = params.data[params.colDef.field];
        return value !== null && value !== undefined;
      }
      return true;
    },
    cellClassRules: {
      'cell-edited': (params: any) => {
        if (!params.data) return false;
        const rowId = params.data.id?.toString();
        const entry = this.pendingEdits.get(rowId);
        return !!(entry && params.colDef.field && params.colDef.field in entry.changes);
      },
      'cell-not-editable': (params: any) => {
        if (!params.data || !params.colDef.field) return false;
        const isRateColumn = params.colDef.cellRendererParams?.isRateColumn === true;
        if (!isRateColumn) return false;
        const value = params.data[params.colDef.field];
        return value === null || value === undefined;
      },
    },
  };

  public sideBar: SideBarDef | string | string[] | boolean | null = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: 'filters',
    hiddenByDefault: true,
  };

  public paginationPageSize = 50;
  private readonly maxPaginationPageSize = 500;
  public gridOptions: GridOptions = {
    sideBar: this.sideBar,
    rowModelType: 'serverSide',
    // Keep the block size aligned with the page size (updated in
    // onPaginationChanged). Do NOT set maxBlocksInCache with SSRM pagination:
    // purging blocks the pager still needs breaks the last-page control and
    // can trigger reload loops.
    cacheBlockSize: this.paginationPageSize,
    headerHeight: 45,
    pagination: true,
    paginationPageSize: this.paginationPageSize,
    paginationAutoPageSize: false,
    paginationPageSizeSelector: [
      25,
      50,
      100,
      200,
      500,
    ],
    getRowId: (params) => params.data.id.toString(),
    selectionColumnDef: {
      pinned: 'left',
      lockPosition: 'left',
      lockPinned: true,
      lockVisible: true,
    } as any,
    getRowStyle: (params: any) => {
      if (params.data && this.pendingEdits.has(params.data.id?.toString())) {
        return { background: '#FFF9C4' };
      }
      return undefined;
    },
  };

  public rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
  };

  closeResult: WritableSignal<string> = signal('');

  constructor(private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {
    this.rateListingService.refreshGrid$.subscribe(() => {
      this.handleDataGrid();
    });

    this.getGroups();
    this.getRatesCategories();
    this.checkZones();
  }

  /**
   * DATA FETCHING
   */
  async checkZones() {
    try {
      const response = await this.zoneService.getProviderZones({
        page_size: 1,
        is_active: true,
      });
      this.hasNoZones = response.count === 0;
      if (this.hasNoZones) {
        await AlertUtil.info('you dont have any zones yet, please go to manage location to set zone');
        this.goTo('/instant-rate-manager/provider-locations');
      }
    } catch (error) {
      console.error('Error checking zones:', error);
    }
  }

  async getGroups() {
    this.groupArray = await this.groupService.getGroups({
      name: this.searchGroup || null,
    });
  }

  async getRatesCategories() {
    try {
      const response = await this.rateManagerService.getProviderRatesCategories();
      this.rateCategoryArray = (response?.data ?? []).map((item: any) => ({
        label: this.capitalizeFirstLetter(item.name),
        value: item.id,
      }));
    } catch (err) {
      console.error('Failed to load rate categories:', err);
    }
  }

  /**
   * GRID ACTIONS
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    const datasource: IServerSideDatasource = this.createServerSideDatasource();
    this.setSideBarVisible(true);
    this.gridApi.setGridOption('serverSideDatasource', datasource);
    if (this.savedSearch) this.savedSearch.refresh(this.gridApi);
  }

  onColumnChange(event: any) {
    if (this.savedSearch) {
      this.savedSearch.handleGridEvent(event);
    }
  }

  onColumnVisible(event: any) {
    if (this.savedSearch) this.savedSearch.saveColumnState();
  }

  clearFiltersExternally() {
    this.selectedCategoryId = null;
    this.selectedGroup = null;
  }

  setSideBarVisible(value: boolean) {
    this.gridApi.setSideBarVisible(value);
  }

  onPaginationChanged() {
    if (!this.gridApi) return;
    let newPageSize = this.gridApi.paginationGetPageSize();

    // Clamp to the maximum allowed page size. Guards against a stale/persisted
    // page size (e.g. a previously saved 1000) being restored beyond the cap.
    if (newPageSize > this.maxPaginationPageSize) {
      newPageSize = this.maxPaginationPageSize;
      this.gridApi.setGridOption('paginationPageSize', newPageSize);
    }

    // Always clear selection on any pagination change (page navigation or page size change)
    // to avoid confusing the user with stale selections across pages
    this.clearSelection();

    if (newPageSize !== this.paginationPageSize) {
      this.paginationPageSize = newPageSize;
      this.gridApi.setGridOption('cacheBlockSize', newPageSize);
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  async handleDataGrid() {
    if (this.hasPendingEdits) {
      const result = await AlertUtil.confirm('Unsaved Changes', 'You have pending edits that will be lost. Continue?', 'Yes, discard changes.');
      if (!result.isConfirmed) return;
      this.clearPendingEdits();
    }
    this.gridApi.refreshServerSide({ purge: true });
  }

  private buildPayload(
    rowParams?: IServerSideGetRowsParams,
    extraParams: any = {},
  ) {
    // Prefer AG Grid block bounds so page/pageSize stay aligned with the
    // request even if component state briefly lags the page-size selector.
    const startRow = rowParams?.request?.startRow ?? 0;
    const endRow =
      rowParams?.request?.endRow ?? startRow + this.paginationPageSize;
    const pageSize = Math.max(endRow - startRow, 1);
    const page = Math.floor(startRow / pageSize) + 1;

    let filterModel: string | [] = [];
    let sortModel: string | [] = [];

    if (rowParams?.request?.filterModel && Object.keys(rowParams.request.filterModel).length > 0) {
      filterModel = JSON.stringify(rowParams.request.filterModel);
    } else if (!rowParams && this.gridApi) {
      const currentFilter = this.gridApi.getFilterModel();
      if (currentFilter && Object.keys(currentFilter).length > 0) {
        filterModel = JSON.stringify(currentFilter);
      }
    }

    if (rowParams?.request?.sortModel && Object.keys(rowParams.request.sortModel).length > 0) {
      sortModel = JSON.stringify(rowParams.request.sortModel);
    } else if (!rowParams && this.gridApi) {
      const columnState = this.gridApi.getColumnState();
      const currentSort = columnState
        .filter((col: any) => col.sort)
        .map((col: any) => ({
          colId: col.colId,
          sort: col.sort,
          sortIndex: col.sortIndex,
        }))
        .sort((a: any, b: any) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
      if (currentSort.length > 0) {
        sortModel = JSON.stringify(currentSort);
      }
    }

    return {
      page,
      pageSize,
      sortModel,
      filterModel,
      categoryId: this.selectedCategoryId,
      groupId: this.selectedGroup,
      ...extraParams,
    };
  }

  createServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (rowParams: IServerSideGetRowsParams) => {
        const payload = this.buildPayload(rowParams);

        this.rateManagerService.getRatesAgGrid(payload).subscribe({
          next: (response: any) => {
            const data = response?.rows ?? [];
            const lastRow = response?.lastRow ?? 0;
            this.totalRates = response?.total ?? 0;

            if (!data || data.length === 0) {
              // Report the real row count (not 0) so the pager stays intact when
              // an empty block comes back but earlier pages still hold data.
              rowParams.success({ rowData: [], rowCount: lastRow });
              if (!lastRow) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
                // Requested a page past the end (e.g. rows deleted) — snap back
                // to the last valid page so the user isn't stranded on a blank one.
                const lastPage = Math.max(
                  Math.ceil(lastRow / this.paginationPageSize) - 1,
                  0,
                );
                if (this.gridApi.paginationGetCurrentPage() > lastPage) {
                  this.gridApi.paginationGoToPage(lastPage);
                }
              }
              return;
            }

            const transformedData = this.rateListingService.transformData(data);
            const newColumnDefs = this.rateListingService.getColumnDefs(data);

            // Compare column field names to avoid unnecessary grid re-renders
            const currentFields = this.columnDefs
              .map((c) => c.field)
              .sort()
              .join(',');
            const newFields = newColumnDefs
              .map((c) => c.field)
              .sort()
              .join(',');

            if (currentFields !== newFields) {
              if (this.savedSearch) this.savedSearch.skipEventHandler = true;

              // Capture the current live state before factory reset
              const lastKnownColumnState = this.gridApi.getColumnState();
              const lastKnownFilterModel = this.gridApi.getFilterModel();

              // Pre-inject the sort state into the new definitions so AG Grid
              // never sees an 'unsorted' transition state when setting the new columns.
              newColumnDefs.forEach((newCol: any) => {
                const oldCol = lastKnownColumnState.find((c: any) => c.colId === newCol.field || c.colId === newCol.colId);
                if (oldCol && oldCol.sort) {
                  newCol.sort = oldCol.sort;
                  newCol.sortIndex = oldCol.sortIndex;
                }
              });

              this.columnDefs = newColumnDefs;
              this.gridApi.setGridOption('columnDefs', newColumnDefs);

              // Re-apply the live state synchronously to handle column order, width, and pinning
              this.gridApi.applyColumnState({
                state: lastKnownColumnState,
                applyOrder: true,
              });
              this.gridApi.setFilterModel(lastKnownFilterModel);

              if (this.savedSearch) {
                setTimeout(() => {
                  this.savedSearch.skipEventHandler = false;
                }, 500);
              }
            }

            rowParams.success({ rowData: transformedData, rowCount: lastRow });
            this.gridApi.hideOverlay();
          },
          error: (error: any) => {
            console.error('Error fetching rates:', error);
            rowParams.fail();
            this.gridApi.showNoRowsOverlay();
          },
        });
      },
    };
  }

  resizeColumns = () => {
    const columns = this.gridApi.getColumns();
    if (columns) {
      const allColumnIds = columns.map((column: any) => column.getColId());
      this.gridApi.autoSizeColumns(allColumnIds, false);
    }
  };

  /**
   * ACTIONS
   */
  openCreateRateModal = () => {
    const modalRef = this.modalService.open(CreateRateModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.handleDataGrid();
      }
    });
  };

  openBulkCreateRateModal = async () => {
    if (this.hasNoZones) {
      await AlertUtil.info('You must have at least one zone set up before you can import rates.');
      this.goTo('/instant-rate-manager/provider-locations');
      return;
    }

    const modalRef = this.modalService.open(BulkCreateRateModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.closed.subscribe(() => {
      this.handleDataGrid();
    });
  };

  createNewGroup = async () => {
    const trimmedName = this.newGroupName.trim();
    if (!trimmedName) return;

    try {
      const response = await this.groupService.createNewGroup({
        name: trimmedName,
      });

      const newGroup = {
        value: response?.data?.id,
        label: response?.data?.name,
      };

      this.suppressGridRefresh = true;
      this.groupArray = [
        newGroup,
        ...this.groupArray,
      ];
      this.cancelAddGroup();
    } catch (err) {
      console.error('Error creating group:', err);
    }
  };

  onDeleteGroup = async (item: any, event: Event) => {
    event.stopPropagation();
    const result = await AlertUtil.confirm('Delete Group', `Are you sure you want to delete <b class="text-danger">${item.label}</b>?`, 'Yes, delete it.');

    if (result.isConfirmed) {
      try {
        const response = await this.groupService.deleteGroup(item.value);

        if (response?.status === 200 || response?.success) {
          this.groupArray = this.groupArray.filter((g) => g.value !== item.value);
          AlertUtil.success('Group has been removed successfully.', 'Deleted!');
          this.handleDataGrid();
        } else {
          AlertUtil.error('Failed to delete the group.');
        }
      } catch (err: any) {
        AlertUtil.error(err?.error.message || 'An unexpected error occurred.');
      }
    }
  };

  getSelectedRouteRateIds(): number[] {
    return this.getSelectedRowNodes().flatMap((node: any) => node.data?.route_rate_ids || []);
  }

  private getSelectedRowNodes(): any[] {
    if (!this.gridApi) return [];

    const selectionState = this.gridApi.getServerSideSelectionState?.();
    if (selectionState) {
      const nodes: any[] = [];
      const toggled = new Set(selectionState.toggledNodes ?? []);
      const displayedCount = this.gridApi.getDisplayedRowCount();

      for (let i = 0; i < displayedCount; i++) {
        const node = this.gridApi.getDisplayedRowAtIndex(i);
        if (!node?.data) continue;

        const isSelected = selectionState.selectAll ? !toggled.has(node.id!) : toggled.has(node.id!);

        if (isSelected) {
          nodes.push(node);
        }
      }

      return nodes;
    }

    return this.gridApi.getSelectedNodes() ?? [];
  }

  onSelectionChanged(): void {
    this.selectedRowCount = this.getSelectedRowNodes().length;
  }

  clearSelection(): void {
    if (!this.gridApi) return;
    // Reset server-side selection state (clears the "select all" header checkbox)
    this.gridApi.setServerSideSelectionState?.({
      selectAll: false,
      toggledNodes: [],
    });
    this.gridApi.deselectAll();
    this.selectedRowCount = 0;
  }

  getBulkUpdateFields(): BulkUpdateField[] {
    const excluded = new Set([
      'action',
      'status',
      'route_rate_ids',
      'created_at',
      'updated_at',
      'rate_category',
      'origin_name',
      'destination_name',
      'group',
    ]);
    const fields: BulkUpdateField[] = [
      {
        field: 'transit_days',
        apiKey: 'transit_days',
        label: 'Transit Days',
        type: 'number',
      },
    ];

    this.columnDefs.forEach((col) => {
      const field = col.field;
      if (!field || excluded.has(field) || fields.some((item) => item.field === field)) {
        return;
      }

      const apiKey = col.cellRendererParams?.apiKey || (field === 'rate_unit' ? 'unit' : field);
      let type: BulkUpdateField['type'] = 'text';
      let options: string[] | undefined;

      if (field === 'rate_unit') {
        type = 'select';
        options = [
          'per_unit',
          'per_kg',
        ];
      } else if (field === 'pickup_type' || field === 'delivery_type') {
        type = 'select';
        options = [
          'door',
          'depot',
        ];
      } else if (col.cellRendererParams?.isRateColumn || field.startsWith('bracket_')) {
        type = 'number';
      }

      fields.push({
        field,
        apiKey,
        label: col.headerName || this.capitalizeFirstLetter(field.replace(/_/g, ' ')),
        type,
        options,
      });
    });

    return fields;
  }

  openBulkUpdateModal(): void {
    if (!this.getSelectedRowNodes().length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    this.bulkUpdateField = '';
    this.bulkUpdateValue = '';
    this.bulkTransitDaysMode = 'update';
    this.modalService.open(this.bulkUpdateModal, {
      centered: true,
      size: 'md',
    });
  }

  onBulkUpdateFieldChange(): void {
    if (this.bulkUpdateField !== 'transit_days') {
      this.bulkTransitDaysMode = 'update';
    }

    this.bulkUpdateValue = this.selectedBulkFieldConfig?.type === 'select' ? (this.selectedBulkFieldConfig.options?.[0] ?? '') : '';
  }

  applyBulkUpdate(modal: any): void {
    const fieldConfig = this.selectedBulkFieldConfig;
    const selectedNodes = this.getSelectedRowNodes();

    if (!fieldConfig || !selectedNodes.length) {
      AlertUtil.info('Please select at least one rate and a field to update.');
      return;
    }

    if (this.bulkUpdateValue === '' || this.bulkUpdateValue === null || this.bulkUpdateValue === undefined) {
      AlertUtil.info('Please enter a value to apply.');
      return;
    }

    const parsedValue = fieldConfig.type === 'number' ? Number(this.bulkUpdateValue) : this.bulkUpdateValue;
    if (fieldConfig.type === 'number' && Number.isNaN(parsedValue)) {
      AlertUtil.info('Please enter a valid number.');
      return;
    }

    const updatedNodes: any[] = [];
    const skippedCount: number[] = [];

    selectedNodes.forEach((node: any) => {
      const field = fieldConfig.field;
      const oldValue = node.data[field];

      // Skip rows where the column doesn't apply (null/undefined means this
      // field isn't relevant for this rate's category, e.g. a bracket column
      // that doesn't exist for cars)
      if ((fieldConfig.type === 'number' || field.startsWith('bracket_')) && (oldValue === null || oldValue === undefined)) {
        skippedCount.push(1);
        return;
      }

      let newValue = parsedValue;

      if (field === 'transit_days' && this.bulkTransitDaysMode === 'add_on_top') {
        newValue = Number(oldValue ?? 0) + Number(parsedValue);
      }

      if (newValue === oldValue) return;

      this.trackPendingEdit(node, field, newValue, oldValue);
      node.setData({ ...node.data, [field]: newValue });
      updatedNodes.push(node);
    });

    if (!updatedNodes.length) {
      const skipped = skippedCount.length;
      if (skipped > 0) {
        AlertUtil.info(`${skipped} rate(s) were skipped because the selected field does not apply to them.`);
      } else {
        AlertUtil.info('Selected rates already have this value.');
      }
      return;
    }

    this.gridApi.redrawRows({ rowNodes: updatedNodes });
    modal.close();

    const skipped = skippedCount.length;
    if (skipped > 0) {
      AlertUtil.info(`Updated ${updatedNodes.length} rate(s). ${skipped} rate(s) were skipped because the field does not apply to them.`);
    }

    this.clearSelection();
  }

  readonly bulkStatusOptions: {
    label: string;
    value: string;
    icon: string;
    btnClass: string;
  }[] = [
    {
      label: 'Publish',
      value: 'published',
      icon: 'bi-check-circle',
      btnClass: 'btn-success',
    },
    {
      label: 'Unpublish',
      value: 'unpublished',
      icon: 'bi-eye-slash',
      btnClass: 'btn-warning',
    },
    {
      label: 'Activate',
      value: 'active',
      icon: 'bi-lightning',
      btnClass: 'btn-success',
    },
    {
      label: 'Deactivate',
      value: 'deactivated',
      icon: 'bi-x-circle',
      btnClass: 'btn-danger',
    },
    {
      label: 'Draft',
      value: 'draft',
      icon: 'bi-pencil-square',
      btnClass: 'btn-secondary',
    },
  ];

  async bulkSubmit() {
    if (this.hasPendingEdits) {
      AlertUtil.info('You have pending changes. Please submit or discard them before changing status.');
      return;
    }

    const selectedNodes = this.getSelectedRowNodes();
    if (!selectedNodes.length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    // Only draft rates can be submitted for review
    const draftNodes = selectedNodes.filter((node: any) => node.data?.status?.toLowerCase() === 'draft');

    if (!draftNodes.length) {
      AlertUtil.info('Only rates in Draft status can be submitted for review.');
      return;
    }

    const skipped = selectedNodes.length - draftNodes.length;
    const confirmMsg =
      skipped > 0
        ? `${draftNodes.length} draft rate(s) will be submitted for review. ${skipped} non-draft rate(s) will be skipped.`
        : `Are you sure you want to submit ${draftNodes.length} rate(s) for review?`;

    const result = await AlertUtil.confirm('Submit for Review', confirmMsg, 'Yes, submit.');

    if (!result.isConfirmed) return;

    const rates = draftNodes.map((node: any) => ({
      route_rate_ids: node.data?.route_rate_ids || [],
      updates: { status: 'in_review' },
    }));

    this.rateManagerService.updateRateStatus(rates).subscribe({
      next: (response: any) => {
        AlertUtil.success(response.message || 'Rates have been submitted for review.', 'Submitted!');
        this.clearSelection();
        this.handleDataGrid();
      },
      error: () => {
        AlertUtil.error('Failed to submit the rates.');
      },
    });
  }

  async bulkDeactivate() {
    if (this.hasPendingEdits) {
      AlertUtil.info('You have pending changes. Please submit or discard them before changing status.');
      return;
    }

    const selectedNodes = this.getSelectedRowNodes();
    if (!selectedNodes.length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    // Only published rates can be deactivated
    const eligibleNodes = selectedNodes.filter((node: any) => {
      const status = node.data?.status?.toLowerCase();
      return status === 'published';
    });

    if (!eligibleNodes.length) {
      AlertUtil.info('Only rates in Published status can be deactivated.');
      return;
    }

    const skipped = selectedNodes.length - eligibleNodes.length;
    const confirmMsg =
      skipped > 0 ? `${eligibleNodes.length} rate(s) will be deactivated. ${skipped} ineligible rate(s) will be skipped.` : `Are you sure you want to deactivate ${eligibleNodes.length} rate(s)?`;

    const result = await AlertUtil.confirm('Deactivate Rates', confirmMsg, 'Yes, deactivate.');

    if (!result.isConfirmed) return;

    const rates = eligibleNodes.map((node: any) => ({
      route_rate_ids: node.data?.route_rate_ids || [],
      updates: { status: 'deactivated' },
    }));

    this.rateManagerService.updateRateStatus(rates).subscribe({
      next: (response: any) => {
        AlertUtil.success(response.message || 'Rates have been deactivated.', 'Deactivated!');
        this.clearSelection();
        this.handleDataGrid();
      },
      error: () => {
        AlertUtil.error('Failed to deactivate the rates.');
      },
    });
  }

  async bulkMoveToDraft() {
    if (this.hasPendingEdits) {
      AlertUtil.info('You have pending changes. Please submit or discard them before changing status.');
      return;
    }

    const selectedNodes = this.getSelectedRowNodes();
    if (!selectedNodes.length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    // Only in_review, published, or deactivated rates can be moved back to draft
    const eligibleNodes = selectedNodes.filter((node: any) => {
      const status = node.data?.status?.toLowerCase();
      return status === 'in review' || status === 'published' || status === 'deactivated';
    });

    if (!eligibleNodes.length) {
      AlertUtil.info('Only rates in In Review, Published, or Deactivated status can be moved to Draft.');
      return;
    }

    const skipped = selectedNodes.length - eligibleNodes.length;
    const confirmMsg =
      skipped > 0
        ? `${eligibleNodes.length} rate(s) will be moved to Draft. ${skipped} rate(s) already in Draft will be skipped.`
        : `Are you sure you want to move ${eligibleNodes.length} rate(s) back to Draft?`;

    const result = await AlertUtil.confirm('Move to Draft', confirmMsg, 'Yes, move to Draft.');

    if (!result.isConfirmed) return;

    const rates = eligibleNodes.map((node: any) => ({
      route_rate_ids: node.data?.route_rate_ids || [],
      updates: { status: 'draft' },
    }));

    this.rateManagerService.updateRateStatus(rates).subscribe({
      next: (response: any) => {
        AlertUtil.success(response.message || 'Rates have been moved to Draft.', 'Done!');
        this.clearSelection();
        this.handleDataGrid();
      },
      error: () => {
        AlertUtil.error('Failed to move rates to Draft.');
      },
    });
  }

  async bulkUpdateStatus(status: string) {
    const selectedNodes = this.getSelectedRowNodes();
    if (!selectedNodes.length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    const option = this.bulkStatusOptions.find((o) => o.value === status);
    const label = option?.label || status;

    const result = await AlertUtil.confirm(`${label} Rates`, `Are you sure you want to <b>${label.toLowerCase()}</b> ${selectedNodes.length} rate(s)?`, `Yes, ${label.toLowerCase()} them.`);

    if (!result.isConfirmed) return;

    const rates = selectedNodes.map((node: any) => ({
      route_rate_ids: node.data?.route_rate_ids || [],
      updates: { status },
    }));

    this.rateManagerService.updateRateStatus(rates).subscribe({
      next: (response: any) => {
        AlertUtil.success(response.message || `Rates have been ${label.toLowerCase()}ed successfully.`, 'Success!');
        this.gridApi.deselectAll();
        this.handleDataGrid();
      },
      error: () => {
        AlertUtil.error(`Failed to ${label.toLowerCase()} the rates.`);
      },
    });
  }

  async bulkDelete() {
    if (this.hasPendingEdits) {
      AlertUtil.info('You have pending changes. Please submit or discard them before deleting.');
      return;
    }

    const selectedNodes = this.getSelectedRowNodes();
    if (!selectedNodes.length) {
      AlertUtil.info('Please select at least one rate.');
      return;
    }

    const ids = selectedNodes.flatMap((node: any) => node.data?.route_rate_ids || []);
    if (!ids.length) {
      AlertUtil.info('No rate IDs found for the selected rows.');
      return;
    }

    const result = await AlertUtil.confirm('Delete Rates', `Are you sure you want to delete ${selectedNodes.length} rate(s)?`, 'Yes, delete them.');

    if (result.isConfirmed) {
      this.rateManagerService.deleteRate(ids).subscribe({
        next: (response: any) => {
          AlertUtil.success(response.message || 'Rates have been deleted successfully.', 'Deleted!');
          this.gridApi.deselectAll();
          this.handleDataGrid();
        },
        error: () => {
          AlertUtil.error('Failed to delete the rates.');
        },
      });
    }
  }

  onCellValueChanged(event: any): void {
    const field = event.colDef.field;
    const newValue = event.newValue;
    const oldValue = event.oldValue;

    if (newValue === oldValue) return;

    this.trackPendingEdit(event.node, field, newValue, oldValue);
    this.gridApi.redrawRows({
      rowNodes: [
        event.node,
      ],
    });
  }

  private trackPendingEdit(node: any, field: string, newValue: any, oldValue: any): void {
    const rowId = node.data.id?.toString();

    let entry = this.pendingEdits.get(rowId);
    if (!entry) {
      entry = {
        routeRateIds: node.data.route_rate_ids || [],
        changes: {},
        originalValues: {},
      };
      this.pendingEdits.set(rowId, entry);
    }

    if (!(field in entry.originalValues)) {
      entry.originalValues[field] = oldValue;
    }

    if (newValue === entry.originalValues[field]) {
      delete entry.changes[field];
      delete entry.originalValues[field];
      if (field === 'origin_name') delete entry.changes['origin_id'];
      if (field === 'destination_name') delete entry.changes['destination_id'];
      if (Object.keys(entry.changes).length === 0) {
        this.pendingEdits.delete(rowId);
      }
    } else {
      entry.changes[field] = newValue;
      if (field === 'origin_name' && node.data.origin_id) {
        entry.changes['origin_id'] = node.data.origin_id;
      }
      if (field === 'destination_name' && node.data.destination_id) {
        entry.changes['destination_id'] = node.data.destination_id;
      }
    }
  }

  async submitPendingEdits(): Promise<void> {
    if (!this.hasPendingEdits) return;

    const result = await AlertUtil.confirm('Submit Changes', `You are about to update ${this.pendingEdits.size} rate(s). Continue?`, 'Yes, update them.');
    if (!result.isConfirmed) return;

    // Build a field-to-apiKey map from current column defs
    const fieldToApiKey: Record<string, string> = {};
    this.columnDefs.forEach((col: any) => {
      if (col.cellRendererParams?.apiKey) {
        fieldToApiKey[col.field] = col.cellRendererParams.apiKey;
      }
    });
    // Static field mappings
    fieldToApiKey['rate_unit'] = 'unit';
    fieldToApiKey['origin_name'] = 'origin_name';
    fieldToApiKey['destination_name'] = 'destination_name';

    const rates: { route_rate_ids: number[]; updates: Record<string, any> }[] = [];

    this.pendingEdits.forEach((entry) => {
      const apiUpdates: Record<string, any> = {};
      for (const [
        field,
        value,
      ] of Object.entries(entry.changes)) {
        const apiKey = fieldToApiKey[field] || field;
        apiUpdates[apiKey] = value;
      }
      rates.push({
        route_rate_ids: entry.routeRateIds,
        updates: apiUpdates,
      });
    });

    try {
      await firstValueFrom(this.rateManagerService.bulkUpdateRates(rates));
      AlertUtil.success('Rates updated successfully.', 'Success!');

      // Collect edited row nodes and their edited columns for flash
      const rowNodes: any[] = [];
      const flashColumns: string[] = [];

      this.pendingEdits.forEach((entry, rowId) => {
        const rowNode = this.gridApi.getRowNode(rowId);
        if (rowNode) {
          rowNodes.push(rowNode);
          Object.keys(entry.changes).forEach((col) => {
            if (!flashColumns.includes(col)) flashColumns.push(col);
          });
        }
      });

      // Clear pending edits (removes yellow highlight)
      this.pendingEdits.clear();
      this.gridApi.redrawRows({ rowNodes });

      // Flash edited cells green to indicate success
      this.gridApi.flashCells({
        rowNodes,
        columns: flashColumns,
        flashDuration: 500,
        fadeDuration: 1500,
      });
    } catch (err: any) {
      AlertUtil.error(err?.error?.message || 'Failed to update rates.');
    }
  }

  discardPendingEdits(): void {
    this.pendingEdits.forEach((entry, rowId) => {
      const rowNode = this.gridApi.getRowNode(rowId);
      if (rowNode) {
        const revertedData = { ...rowNode.data };
        for (const field in entry.originalValues) {
          revertedData[field] = entry.originalValues[field];
        }
        rowNode.setData(revertedData);
      }
    });
    this.clearPendingEdits();
  }

  private clearPendingEdits(): void {
    this.pendingEdits.clear();
    this.gridApi.redrawRows();
  }

  private getVisibleColumns(): Record<string, string[]> {
    if (!this.gridApi) return { common: [] };

    const columnState = this.gridApi.getColumnState();
    const visibleColIds = columnState.filter((col: any) => !col.hide && col.colId).map((col: any) => col.colId as string);

    const columns: { [key: string]: string[] } = { common: [] };
    const excluded = new Set([
      'action',
      'ag-Grid-ControlsColumn',
      'ag-Grid-SelectionColumn',
    ]);

    visibleColIds.forEach((colId: string) => {
      if (excluded.has(colId) || colId.startsWith('ag-Grid-')) return;

      const colDef = this.columnDefs.find((cd) => cd.field === colId || (cd as any).colId === colId);

      const rendererParams = colDef?.cellRendererParams;
      const isRateColumn = rendererParams?.isRateColumn === true;

      if (isRateColumn && rendererParams?.apiKey) {
        // Bracket column (pallet/weight-based) — group under its rate category and pass the bracket ID
        const category = rendererParams.rateCategory || 'pallet';
        if (!columns[category]) columns[category] = [];
        columns[category].push(rendererParams.bracketId);
      } else if (isRateColumn && !rendererParams?.apiKey) {
        // Rate-type column — group under its rate category (e.g. 'car')
        const category = rendererParams?.rateCategory || colId;
        if (!columns[category]) columns[category] = [];
        columns[category].push(colId);
      } else {
        columns['common'].push(colId);
      }
    });

    return columns;
  }

  downloadCsvRate = (mode: 'all' | 'current' = 'all') => {
    const columns = this.getVisibleColumns();
    const payload = this.buildPayload(undefined, {
      download_csv: true,
      columns: JSON.stringify(columns),
    });

    if (mode === 'all') {
      // Remove pagination params so the backend exports ALL matching records
      delete payload.page;
      delete payload.pageSize;
    } else if (this.gridApi) {
      // Use the grid's live page size and current page for current-page export
      const livePageSize = this.gridApi.paginationGetPageSize();
      const currentPage = this.gridApi.paginationGetCurrentPage();
      payload.pageSize = livePageSize;
      payload.page = currentPage + 1;
    }

    this.rateManagerService.getRatesAgGrid(payload).subscribe({
      next: (csvText: string) => {
        const blob = new Blob(
          [
            csvText,
          ],
          { type: 'text/csv' },
        );
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
        link.download = `rates_${timestamp}.csv`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      },
      error: (err: any) => {
        console.error('Failed to download CSV:', err);
        AlertUtil.error('Failed to export rates. Please try again.');
      },
    });
  };

  /**
   * UI HELPERS
   */
  cancelAddGroup = () => {
    this.isAddingGroup = false;
    this.newGroupName = '';
  };

  onGroupChange = (group: any) => {
    if (this.suppressGridRefresh) {
      this.suppressGridRefresh = false;
      return;
    }
    this.selectedGroup = group?.value ?? null;
    this.handleDataGrid();
  };

  onRateCategoryChange = (category: any) => {
    this.selectedCategoryId = category?.value ?? null;
    this.clearSelection();
    this.handleDataGrid();
  };

  capitalizeFirstLetter = (text: string): string => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };
}
